<template>
  <div class="table-section col-12">
    <div class="table-top">
      <b-row>
        <b-col md="4" lg="4" class="my-1">
          <p class="table-filter text-left">
            <BaseInput
              type="text"
              label="Search"
              :inputType="'text'"
              v-model="filter"
              placeholder="Search something..."
              :hasPreIcon="false"
            >
            </BaseInput>
          </p>
        </b-col>
        <b-col md="8" lg="8" class="my-1">
          <p class="table-action text-right">
            <span class="excel-download-btn-hold">
              <button
                type="button"
                class="btn btn-primary btn-semi-rounded mb-2 mr-2"
              >
                <i class="bx bxs-report"></i> &nbsp; &nbsp;
                <download-excel
                  class="excel-download-btn"
                  :data="items"
                  :fields="json_fields"
                  worksheet="My Worksheet"
                  name="filename.xls"
                >
                  Download As Excel
                </download-excel>
              </button>
            </span>
          </p>
        </b-col>
      </b-row>
    </div>

    <b-table
      striped
      hover
      :fields="fields"
      :items="items"
      :busy="isBusy"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      sort-icon-left
      responsive="sm"
      :current-page="currentPage"
      :per-page="perPage"
      :filter="filter"
      :filterIncludedFields="filterOn"
      :sort-direction="sortDirection"
      @filtered="onFiltered"
    >
      <!-- A virtual column -->
      <template v-slot:cell(index)="data">
        {{ data.index + 1 }}
      </template>

      <!-- A virtual composite column -->
      <template v-slot:cell(accountbankCode)="data">
        <p class="name-field">
          <span class="">{{ data.item.accountBankCode }}</span>
        </p>
      </template>

      <!-- A custom formatted column -->
      <template v-slot:cell(accountName)="data">
        <p class="name-field">
          <span>{{ data.item.accountName }}</span>
        </p>
      </template>

      <!-- A virtual composite column -->
      <template v-slot:cell(accountInterestRate)="data">
        <p class="name-field">
          <span class="">{{ data.item.accountInterestRate }}</span>
        </p>
      </template>

      <!-- A virtual composite column -->
      <template v-slot:cell(accountType)="data">
        <p class="name-field">
          <span class="">{{ data.item.accountType }}</span>
        </p>
      </template>

      <!-- A virtual composite column -->
      <template v-slot:cell(accountCurrency)="data">
        <p class="name-field">
          <span class="">{{ data.item.accountCurrency }}</span>
        </p>
      </template>

      <!-- A virtual composite column -->
      <template v-slot:cell(accountNumber)="data">
        <p class="name-field">
          <span class="">{{ data.item.accountNumber }}</span>
        </p>
      </template>

      <template v-slot:cell(accountBalance)="data">
        <p class="name-field">
          <span class=""
            >₦{{ data.item.accountBalance | newAmountFormatter }}</span
          >
        </p>
      </template>

      <template v-slot:cell(accountCustomerId)="data">
        <p class="name-field">
          <span class="">{{ data.item.accountCustomerId }}</span>
        </p>
      </template>

      <!-- A virtual composite column -->
      <template v-slot:cell(accountStatus)="data">
        <p class="name-field">
          <span
            v-if="data.item.accountStatus === 'ACTIVE'"
            class="badge badge-pill badge-soft-success font-size-12"
            >{{ data.item.accountStatus }}</span
          >
          <span
            v-if="data.item.accountStatus === 'INACTIVE'"
            class="badge badge-pill badge-soft-danger font-size-12"
            >{{ data.item.accountStatus }}</span
          >
          <span
            v-if="data.item.accountStatus === 'PENDING'"
            class="badge badge-pill badge-soft-warning font-size-12"
            >{{ data.item.accountStatus }}</span
          >
        </p>
      </template>

      <!-- Optional default data cell scoped slot -->
      <template v-slot:cell()="data">
        <i>{{ data.value }}</i>
      </template>
    </b-table>

    <b-row>
      <b-col sm="5" md="4" class="my-1">
        <b-form-group
          label="Per page"
          label-cols-sm="6"
          label-cols-md="4"
          label-cols-lg="3"
          label-align-sm="right"
          label-size="sm"
          label-for="perPageSelect"
          class="mb-0"
        >
          <b-form-select
            v-model="perPage"
            id="perPageSelect"
            size="sm"
            :options="pageOptions"
          ></b-form-select>
        </b-form-group>
      </b-col>

      <b-col sm="7" md="8" class="my-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="fill"
          size="sm"
          class="my-0"
        ></b-pagination>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "AllBanksTable",

  data() {
    return {
      sortBy: "date",
      sortDesc: false,
      totalRows: 1,
      currentPage: 1,
      perPage: 5,
      pageOptions: [5, 10, 15],
      sortDirection: "asc",
      filter: null,
      filterOn: [],
      // Note 'isActive' is left out and will not appear in the rendered table
      fields: [
        // A virtual column that doesn't exist in items
        { key: "index", label: "S/N", sortable: true, class: "text-center" },

        // A column that needs custom formatting
        { key: "accountBankCode", label: "Bank Code", sortable: true },
        { key: "accountName", label: "Account Name", sortable: true },
        { key: "accountInterestRate", label: "Interest Rate", sortable: true },
        { key: "accountType", label: "Account Type", sortable: true },
        { key: "accountClass", label: "Account Class", sortable: true },
        { key: "accountCurrency", label: "Currency", sortable: true },
        { key: "accountBalance", label: "Account Balance", sortable: true },
        { key: "accountNumber", label: "Account Number", sortable: true },
        { key: "accountCustomerId", label: "Customer Id", sortable: true },
        { key: "accountStatus", label: "Status", sortable: true }
      ],

      json_fields: {
        "Account Bank Code": "accountBankCode",
        "GL Level1 Code": "accountGlLevel1Code",
        "Account Name": "accountName",
        "Account Interest Rate": "accountInterestRate",
        "Account Type": "accountType",
        "Account Currency": "accountCurrency",
        "Account Number": "accountNumber",
        "Account LastAccess Timestamp": "accountLastAccessTimestamp",
        "Account Date Closed": "accountDateClosed",
        "Account GlCategory": "accountGlCategory",
        "Account Branch Code": "accountBranchCode",
        "Account Status": "accountStatus",
        "Account Id": "accountId",
        "Account Created Date": "accountCreatedDate",
        "Account Customer Id": "accountCustomerId",
        "Account Class": "accountClass",
        "Account GlLevel2 Code": "accountGlLevel2Code",
        "Account Balance": "accountBalance",
        "Account Date Opened": "accountDateOpened"
      },
      json_meta: [
        [
          {
            key: "charset",
            value: "utf-8"
          }
        ]
      ]
    };
  },
  computed: {
    ...mapState({
      items: state => state.internalAccount.internalAccounts,
      isBusy: state => state.internalAccount.moduleLoadingThree
    }),
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => {
          return { text: f.label, value: f.key };
        });
    }
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.items.length;
    this.filter = " ";
  },
  methods: {
    returnItems() {
      // eslint-disable-next-line no-console
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    }
  },

  created() {
    const payload = {
      accountBankCode: this.$store.getters["user/getUserBankCode"],
      readAll: "YES"
    };
    this.$store.dispatch("internalAccount/allInternalAccounts", payload);
  }
};
</script>

<style scoped>
/*.VueTables__date-filter {*/
/*  border: 1px solid #ccc;*/
/*  padding: 6px;*/
/*  border-radius: 4px;*/
/*  cursor: pointer;*/
/*}*/
</style>
